import React, { useEffect } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import mixpanel from "mixpanel-browser";
import { toast } from "react-toastify";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import Tag from "../../../../ui-components/molecules/tag/tag";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import { FactoredInvoiceAttempt, FactoredInvoiceDetail } from "../../../../app/data/invoice-factoring/models";
import {
  ATTEMPT_DOCUMENT_OUTCOME_LABELS,
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  DOCUMENT_STATUS_COLORS,
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_TYPE_LABELS,
  FACTORING_POLL_MS,
  FACTORING_QUERY_OPTIONS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  ITEM_STATUS_COLORS,
  ITEM_STATUS_LABELS,
  formatBillDate,
  formatCents,
  formatDateTime,
} from "../constants";
import { getInvoiceNextAction } from "../utils/nextAction";
import { InvoiceFactoringPath, InvoiceFactoringPathParams } from "../route";
import { useFactoringContext } from "../common/useFactoringContext";
import NextActionChip from "../common/nextActionChip";
import ActivityList from "../common/activityList";
import AttemptSteps from "../common/attemptSteps";
import JsonBlock from "../common/jsonBlock";
import "../../../../ui-components/tabs/tabs.scss";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const renderAttemptPayloads = (attempt: FactoredInvoiceAttempt) => (
  <div className="xgs-invoice-factoring__payloads">
    <JsonBlock title="Request sent to Denim" value={attempt.requestPayload} />
    <JsonBlock title="Response from Denim" value={attempt.responseBody} emptyText="No response body recorded." />
  </div>
);

const DocumentsTable: React.FC<{ invoice: FactoredInvoiceDetail }> = ({ invoice }) => {
  if (!invoice.documents.length) {
    return <div className="xgs-invoice-factoring__muted">No proof of delivery documents were found for this probill yet.</div>;
  }

  return (
    <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
      <thead>
        <tr>
          <th>Document</th>
          <th>Type</th>
          <th>Source ID</th>
          <th>Status</th>
          <th>Sent</th>
          <th>Attempts</th>
          <th>Found</th>
        </tr>
      </thead>
      <tbody>
        {invoice.documents.map((document) => (
          <tr key={document.id}>
            <td>{document.fileName || "—"}</td>
            <td>{DOCUMENT_TYPE_LABELS[document.type] || document.type}</td>
            <td className="xgs-invoice-factoring__mono">{document.sourceDocumentId}</td>
            <td>
              <Tag mods={{ color: DOCUMENT_STATUS_COLORS[document.status], uppercase: false }}>
                {DOCUMENT_STATUS_LABELS[document.status]}
              </Tag>
            </td>
            <td>{formatDateTime(document.sentAt)}</td>
            <td>
              {document.attemptDocuments.length
                ? document.attemptDocuments
                    .map((entry) => `${ATTEMPT_DOCUMENT_OUTCOME_LABELS[entry.outcome]} ${formatDateTime(entry.createdAt)}`)
                    .join("; ")
                : "—"}
            </td>
            <td>{formatDateTime(document.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const FactoredInvoiceDetails: React.FC = () => {
  const history = useHistory();
  const params = useParams() as InvoiceFactoringPathParams;
  const probillNumber = params[InvoiceFactoringPath.probillNumber] as string;
  const queryClient = useQueryClient();
  const { terminalPrefix } = useFactoringContext();

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_invoice_details" });
  }, []);

  const queryKey = [QUERY_KEYS.INVOICE_FACTORING_INVOICE, probillNumber];

  const { data: invoice, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => invoiceFactoringService.getInvoice(probillNumber),
    retry: false,
    refetchInterval: (data) => (data?.isProcessing ? FACTORING_POLL_MS : false),
    ...FACTORING_QUERY_OPTIONS,
  });

  const refreshMutation = useMutation({
    mutationFn: () => invoiceFactoringService.refreshInvoiceStatus(probillNumber),
    onSuccess: (updated) => {
      toast.info(updated.denimJobStatus ? `Denim status: ${updated.denimJobStatus}.` : "Denim status refreshed.");
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_INVOICES]);
    },
    onError: (e: Error) => {
      toast.error(e.message || "Unable to refresh the Denim status.");
      queryClient.invalidateQueries(queryKey);
    },
  });

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_INVOICE_DETAILS}
      isLoading={isLoading}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.INVOICE_FACTORING_INVOICE_DETAILS}
          title={`Probill ${probillNumber}`}
          mods={{ "full-width": true }}
        >
          <Button
            theme={ButtonThemes.blueInverted}
            size={ButtonSizes.auto}
            type="button"
            onClick={() => history.push(`${terminalPrefix}${Routes.billing.invoiceFactoringInvoices}`)}
          >
            All invoices
          </Button>
          {invoice?.denimJobId && (
            <Button
              theme={ButtonThemes.blue}
              size={ButtonSizes.auto}
              type="button"
              spinner={refreshMutation.isLoading}
              disabled={refreshMutation.isLoading}
              onClick={() => refreshMutation.mutate()}
              title="Get the latest job status from Denim"
            >
              Refresh status
            </Button>
          )}
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-invoice-factoring">
        {isError && (
          <div className="xgs-invoice-factoring__error-box">Unable to load the invoice. {(error as Error)?.message}</div>
        )}

        {invoice && (
          <>
            <div className="xgs-invoice-factoring__card">
              <div className="xgs-invoice-factoring__card-head">
                <div className="xgs-invoice-factoring__card-title">
                  <span>
                    Probill{" "}
                    <Link
                      className="xgs-table-link-cell"
                      to={`${terminalPrefix}${Routes.shipments.tracking}/${invoice.probillNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open shipment details in a new tab"
                    >
                      {invoice.probillNumber}
                    </Link>
                  </span>
                  <Tag mods={{ color: INVOICE_STATUS_COLORS[invoice.status], uppercase: false }}>
                    {INVOICE_STATUS_LABELS[invoice.status]}
                  </Tag>
                  <NextActionChip action={getInvoiceNextAction(invoice)} />
                  {invoice.isProcessing && <span className="xgs-invoice-factoring__muted">Sending now…</span>}
                </div>
              </div>

              <div className="xgs-invoice-factoring__summary-grid">
                <dl className="xgs-invoice-factoring__meta">
                  <dt>BOL</dt>
                  <dd>{invoice.bolNumber || "—"}</dd>
                  <dt>Bill date</dt>
                  <dd>{formatBillDate(invoice.billDate)}</dd>
                  <dt>Payor</dt>
                  <dd>
                    {invoice.payorName || "—"}
                    {invoice.payorNumber ? ` (${invoice.payorNumber})` : ""}
                  </dd>
                  <dt>Amount</dt>
                  <dd>{formatCents(invoice.invoiceAmountCents)}</dd>
                  <dt>First tracked</dt>
                  <dd>{formatDateTime(invoice.createdAt)} by {invoice.createdBy}</dd>
                </dl>
                <dl className="xgs-invoice-factoring__meta">
                  <dt>Denim job</dt>
                  <dd className="xgs-invoice-factoring__mono">{invoice.denimJobId || "Not created"}</dd>
                  <dt>Job created</dt>
                  <dd>{formatDateTime(invoice.jobCreatedAt)}</dd>
                  <dt>Denim status</dt>
                  <dd>{invoice.denimJobStatus || "—"}</dd>
                  <dt>Status checked</dt>
                  <dd>{formatDateTime(invoice.denimJobStatusSyncedAt)}</dd>
                  {invoice.statusReason && (
                    <>
                      <dt>Reason</dt>
                      <dd className="xgs-invoice-factoring__text--error">{invoice.statusReason}</dd>
                    </>
                  )}
                </dl>
              </div>
            </div>

            <Tabs>
              <TabList>
                <Tab>Documents ({invoice.documents.length})</Tab>
                <Tab>Attempts ({invoice.attempts.length})</Tab>
                <Tab>Batches ({invoice.batchItems.length})</Tab>
                <Tab>Activity</Tab>
              </TabList>
              <TabPanel>
                <DocumentsTable invoice={invoice} />
              </TabPanel>
              <TabPanel>
                <AttemptSteps
                  attempts={invoice.attempts}
                  newestFirst
                  showDocumentSentAt={false}
                  terminalPrefix={terminalPrefix}
                  emptyText="Nothing has been sent to Denim for this probill yet."
                  renderDetails={renderAttemptPayloads}
                />
              </TabPanel>
              <TabPanel>
                {invoice.batchItems.length ? (
                  <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
                    <thead>
                      <tr>
                        <th>Batch</th>
                        <th>Batch status</th>
                        <th>Row</th>
                        <th>Row status</th>
                        <th>Details</th>
                        <th>Processed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.batchItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <Link
                              className="xgs-table-link-cell"
                              to={`${terminalPrefix}${Routes.billing.invoiceFactoringBatches}/${item.batch.id}`}
                            >
                              #{item.batch.batchNumber} {item.batch.name}
                            </Link>
                          </td>
                          <td>
                            <Tag mods={{ color: BATCH_STATUS_COLORS[item.batch.status], uppercase: false }}>
                              {BATCH_STATUS_LABELS[item.batch.status]}
                            </Tag>
                          </td>
                          <td>{item.rowNumber}</td>
                          <td>
                            <Tag mods={{ color: ITEM_STATUS_COLORS[item.status], uppercase: false }}>
                              {ITEM_STATUS_LABELS[item.status]}
                            </Tag>
                          </td>
                          <td>{item.errorMessage || "—"}</td>
                          <td>{formatDateTime(item.processedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="xgs-invoice-factoring__muted">This probill is not in any batch.</div>
                )}
              </TabPanel>
              <TabPanel>
                <ActivityList entries={invoice.activity} />
              </TabPanel>
            </Tabs>
          </>
        )}
      </div>
    </ContentContainer>
  );
};

export default FactoredInvoiceDetails;
